import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUpIcon, LineChartIcon, CandlestickChartIcon } from "lucide-react";

export interface DifficultyOption {
  id: 'easy' | 'medium' | 'hard';
  title: string;
  description: string;
  icon: React.ReactNode;
  startingBalance: number;
  volatility: number;
}

interface MarketSimulatorDifficultyProps {
  onSelectDifficulty: (difficulty: DifficultyOption) => void;
}

export const MarketSimulatorDifficulty = ({ onSelectDifficulty }: MarketSimulatorDifficultyProps) => {
  const difficulties: DifficultyOption[] = [
    {
      id: 'easy',
      title: 'Beginner Trader',
      description: 'Start with $10,000 and learn the basics in a stable market with low volatility.',
      icon: <TrendingUpIcon className="w-12 h-12" />,
      startingBalance: 10000,
      volatility: 0.5
    },
    {
      id: 'medium',
      title: 'Experienced Investor',
      description: 'Start with $5,000 and test your skills with moderate market fluctuations.',
      icon: <LineChartIcon className="w-12 h-12" />,
      startingBalance: 5000,
      volatility: 1.0
    },
    {
      id: 'hard',
      title: 'Market Expert',
      description: 'Start with $2,500 and prove your expertise in highly volatile conditions.',
      icon: <CandlestickChartIcon className="w-12 h-12" />,
      startingBalance: 2500,
      volatility: 2.0
    }
  ];

  return (
    <div className="container mx-auto p-4 space-y-8">
      <div className="text-center space-y-4 max-w-2xl mx-auto mb-12">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent animate-fade-in">
          Market Conquest
        </h1>
        <p className="text-muted-foreground animate-fade-in delay-100">
          Choose your difficulty and conquer the market!
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {difficulties.map((difficulty, index) => (
          <Card 
            key={difficulty.id}
            className={`
              relative overflow-hidden cursor-pointer transition-all duration-300
              hover:shadow-xl hover:scale-105 group
              animate-fade-in bg-gradient-to-br from-primary/90 to-primary/80
              border-primary/50 text-white
            `}
            style={{ 
              animationDelay: `${index * 100}ms`,
            }}
            onClick={() => onSelectDifficulty(difficulty)}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/10" />
            </div>
            
            <CardHeader>
              <div className="flex flex-col items-center gap-4">
                <div className={`
                  p-4 rounded-full 
                  ${difficulty.id === 'easy' ? 'bg-white/20 text-white' : 
                    difficulty.id === 'medium' ? 'bg-white/20 text-white' : 
                    'bg-white/20 text-white'}
                  group-hover:scale-110 transition-transform duration-300
                `}>
                  {difficulty.icon}
                </div>
                <CardTitle className="text-2xl text-center text-white">
                  {difficulty.title}
                </CardTitle>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <CardDescription className="text-center min-h-[60px] text-white/90">
                {difficulty.description}
              </CardDescription>
              
              <div className="flex justify-center items-center gap-2 text-sm font-medium">
                <div className={`
                  px-3 py-1 rounded-full bg-white/20 text-white
                `}>
                  ${difficulty.startingBalance.toLocaleString()}
                </div>
                <div className={`
                  px-3 py-1 rounded-full bg-white/20 text-white
                `}>
                  {difficulty.id === 'easy' ? 'Low' : 
                   difficulty.id === 'medium' ? 'Medium' : 
                   'High'} Risk
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="text-center text-sm text-muted-foreground mt-8 animate-fade-in delay-300">
        <p>Choose wisely! Each difficulty level offers a unique challenge.</p>
        <p>You can always change the difficulty level later.</p>
      </div>
    </div>
  );
};